import { useMemo } from 'react';
import { Mail, Paperclip, Cpu, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ClassificationBadge } from '@/components/custom/classification-badge';
import type { ClassificationLabelMap } from '@/hooks/use-classification-label';
import type { LabelSource } from './label-provenance';
import { computeLabelProvenance } from './label-provenance';

interface LabelProvenancePopoverProps {
  taskLabels: string[];
  emailClassification: string | null | undefined;
  attachments: Array<{
    fileName: string;
    labels: string[];
    classificationConfidence: number | null;
  }>;
  labelMap: ClassificationLabelMap;
}

const SOURCE_ICON: Record<LabelSource['source'], typeof Mail> = {
  email: Mail,
  attachment: Paperclip,
  pipeline: Cpu,
};

export function LabelProvenancePopover({
  taskLabels,
  emailClassification,
  attachments,
  labelMap,
}: LabelProvenancePopoverProps): React.ReactElement | null {
  const sources = useMemo(
    () => computeLabelProvenance(taskLabels, emailClassification, attachments),
    [taskLabels, emailClassification, attachments]
  );

  if (sources.length === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded p-0.5 cursor-pointer text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
          title="Label sources"
        >
          <Info className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3" align="end">
        <p className="mb-2 text-xs font-medium text-muted-foreground">Label Sources</p>
        <div className="space-y-1.5">
          {sources.map((s) => {
            const Icon = SOURCE_ICON[s.source];
            return (
              <div
                key={`${s.label}-${s.source}-${s.detail}`}
                className="flex items-center gap-2 text-xs"
              >
                <ClassificationBadge classification={s.label} labelMap={labelMap} size="sm" />
                <span className="flex items-center gap-1 text-muted-foreground truncate">
                  <Icon className="h-3 w-3 shrink-0" />
                  <span className="truncate">{s.detail}</span>
                  {s.confidence != null && (
                    <span className="shrink-0">({Math.round(s.confidence * 100)}%)</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
